import { resetBrowseHost } from "@/platform/app-mode/browse-history"
import type { ScreenPresentation } from "@/platform/bridge/types"

type ScreenVisibilityListener = () => void

const listeners = new Set<ScreenVisibilityListener>()

let shown = true
let presentation: ScreenPresentation | null = null
let locked = false
let lockKnown = false
let visibilityKnown = false
let sessionVerified = false
let probesInFlight = 0
let documentEpoch = 0

const hosted = (): boolean => typeof window !== "undefined" && (window as unknown as { NeloBridgeHost?: { protocol?: number } }).NeloBridgeHost?.protocol === 1

export const screenIsVisible = (): boolean => shown && !locked && (!hosted() || (lockKnown && visibilityKnown && sessionVerified))

export const subscribeToScreenVisibility = (listener: ScreenVisibilityListener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const screenIsAwaitingHost = (): boolean => hosted() && !screenIsVisible() && shown && !locked

export const screenHostProgress = (): string => `${lockKnown}:${visibilityKnown}:${sessionVerified}:${presentation}`

export const screenIsProbing = (): boolean => probesInFlight > 0

const snapshot = (): string => `${screenIsVisible()}:${screenIsPainted()}:${screenCanPrepare()}:${screenIsAwaitingHost()}:${screenHostProgress()}:${screenIsProbing()}`

const update = (apply: () => void): void => {
  const before = snapshot()
  apply()
  if (snapshot() === before) return

  for (const listener of listeners) listener()
}

export const setScreenVisible = (next: boolean): void =>
  update(() => {
    visibilityKnown = true
    shown = next
  })

export const setAppLocked = (next: boolean): void =>
  update(() => {
    lockKnown = true
    locked = next
  })

export const notifyHostDeclared = (): void => {
  for (const listener of listeners) listener()
}

export const beginHostedDocument = (): void =>
  update(() => {
    resetBrowseHost()
    shown = true
    presentation = null
    locked = false
    lockKnown = false
    visibilityKnown = false
    sessionVerified = false
    probesInFlight = 0
    documentEpoch += 1
  })

export const beginHostedProbe = (): (() => void) => {
  const epoch = documentEpoch
  update(() => {
    probesInFlight += 1
  })
  let ended = false
  return () => {
    if (ended || epoch !== documentEpoch) return
    ended = true
    update(() => {
      probesInFlight -= 1
    })
  }
}

export const setHostedSessionVerified = (verified: boolean): void =>
  update(() => {
    sessionVerified = verified
  })

export const screenIsPainted = (): boolean => screenIsVisible() || (presentation === "covered" && !locked && lockKnown && sessionVerified)

export const screenCanPrepare = (): boolean => !hosted() || (lockKnown && !locked && presentation !== "suspended")

export const setScreenPresentation = (next: ScreenPresentation): void =>
  update(() => {
    presentation = next
    visibilityKnown = true
    shown = next === "active"
  })
